import { motion } from "framer-motion";
import { Phone, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function HeroSection() {
  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[90vh] md:min-h-screen flex items-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://images.pexels.com/photos/5276374/pexels-photo-5276374.jpeg"
          alt="Mechanic working under a car"
          className="w-full h-full object-cover"
          loading="eager"
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Noise */}
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 md:py-0 w-full">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="uppercase text-xs tracking-[0.2em] text-stone-300 font-medium mb-6"
          >
            Beaverton's Most Trusted Mechanic
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-white mb-6"
          >
            Honest repairs.
            <br />
            <span className="text-primary">Fair prices.</span>
            <br />
            Zero upselling.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="text-base md:text-lg text-stone-300 leading-relaxed max-w-lg mb-10"
          >
            Family-run since day one. We treat every customer like family and every car like our own. No surprises, no unnecessary work — just reliable auto care.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Button
              asChild
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 hover:scale-[1.02] transition-transform font-heading font-bold text-base px-8 h-12"
              data-testid="hero-call-button"
            >
              <a href="tel:5036433040">
                <Phone weight="bold" className="w-5 h-5 mr-2" />
                Call (503) 643-3040
              </a>
            </Button>

            <Button
              variant="outline"
              className="rounded-full border-white/30 text-white bg-white/10 hover:bg-white/20 hover:text-white font-heading font-bold text-base px-8 h-12 transition-colors duration-200"
              data-testid="hero-contact-button"
              onClick={() => {
                const el = document.querySelector("#contact");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Request Appointment
              <ArrowRight weight="bold" className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>

          {/* Trust badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mt-12 flex items-center gap-6 text-stone-400 text-sm"
          >
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
              4.7 Star Rating
            </span>
            <span className="w-px h-4 bg-stone-600" />
            <span>100+ Happy Customers</span>
            <span className="w-px h-4 bg-stone-600 hidden sm:block" />
            <span className="hidden sm:inline">Same-Day Service</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
